'use client';

import { FC, lazy, ReactNode, Suspense } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';

import { BlockWidgetType } from '../../models/Content';
import {
    isBlockWidgetTypeCoverageMap,
    isBlockWidgetTypeExcavationPermit,
    isBlockWidgetTypeFeedbackForm,
    isBlockWidgetTypePlansList,
    isBlockWidgetTypeSpeedTest,
} from './model';
import { transformBlockWidget } from './transformers';

const CoverageMap = lazy(() =>
    import('./widgets/CoverageMap/CoverageMap').then((mod) => ({ default: mod.CoverageMap })),
);
const EmbedIframe = lazy(() =>
    import('./widgets/EmbedIframe/EmbedIframe').then((mod) => ({ default: mod.EmbedIframe })),
);
const ExcavationPermit = lazy(() =>
    import('./widgets/ExcavationPermit/ExcavationPermit').then((mod) => ({ default: mod.ExcavationPermit })),
);
const FeedbackForm = lazy(() =>
    import('./widgets/FeedbackForm/components/FeedbackForm').then((mod) => ({ default: mod.FeedbackForm })),
);
const PublicWebPlans = lazy(() =>
    import('@telialt/core-plans/src/components/Plans/PublicWebPlans').then((mod) => ({ default: mod.PublicWebPlans })),
);
const SpeedTest = lazy(() => import('./widgets/SpeedTest/SpeedTest').then((mod) => ({ default: mod.SpeedTest })));

type Props = {
    contentItem: BlockWidgetType;
    searchParams?: { [key: string]: string | undefined };
};

export const BlockWidgetView: FC<Props> = ({ contentItem: initialContentItem, searchParams }) => {
    const contentItem = useContentfulLiveUpdates(initialContentItem);
    const blockWidget = transformBlockWidget(contentItem);

    let widget: ReactNode = null;

    if (isBlockWidgetTypeCoverageMap(blockWidget)) {
        widget = <CoverageMap />;
    } else if (blockWidget.type === 'EmbedIframe') {
        widget = <EmbedIframe configuration={blockWidget.configuration} />;
    } else if (isBlockWidgetTypeFeedbackForm(blockWidget)) {
        widget = <FeedbackForm />;
    } else if (isBlockWidgetTypePlansList(blockWidget)) {
        const configuration = blockWidget.configuration;

        widget = <PublicWebPlans {...configuration} searchParams={searchParams} />;
    } else if (isBlockWidgetTypeSpeedTest(blockWidget)) {
        widget = <SpeedTest />;
    } else if (isBlockWidgetTypeExcavationPermit(blockWidget)) {
        widget = <ExcavationPermit />;
    }

    return <Suspense fallback={null}>{widget}</Suspense>;
};
